import { z } from 'zod';

/**
 * Journey manifest schema — the connections between the five painted scenes.
 *
 * The manifest is the single source of truth for: which scenes exist, how they
 * connect, and where the walker spawns when crossing between them. SceneRouter
 * loads + validates this at boot. PaintedScene reads its own scene's spec out
 * of the manifest at runtime to know what to render and where the edge zones /
 * interactions live.
 *
 * Coordinate convention: every spatial value is **normalised** to the painted
 * scene's logical canvas (x ∈ [0,1] left→right, y ∈ [0,1] top→bottom). This
 * keeps the manifest resolution-independent and lets PaintedScene project to
 * whatever canvas size the scene's painting renders at.
 */

/** Transition kinds defined by AGE-1421 / scene-transitions-painted §4. */
export const TransitionKindSchema = z.enum([
  /** Walker walks off a frame edge; scene B fades in around her. */
  'edge-crossfade',
  /** Walker steps through a door / portal; vignette + scene swap. */
  'doorway',
  /** Camera zooms into a doorway; new scene reveals through the door opening. */
  'threshold-zoom',
  /** Camera pans horizontally through a shared horizon. */
  'camera-pan',
  /** Both zones stay alive; camera is walker-driven (no fixed duration, no source teardown). */
  'edge-pan',
  /** Painterly noise-mask wash (fallback for cross-realm / forced jumps). */
  'wash',
]);
export type TransitionKind = z.infer<typeof TransitionKindSchema>;

/** A point in [0,1]² normalised scene coords. */
export const NormalisedPointSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
});
export type NormalisedPoint = z.infer<typeof NormalisedPointSchema>;

/**
 * A normalised rect (x,y = top-left, w,h extend down/right). Either `rect` or
 * `polygon` may be used to describe a zone shape — both are normalised.
 */
export const NormalisedRectSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  w: z.number().min(0).max(1),
  h: z.number().min(0).max(1),
});
export type NormalisedRect = z.infer<typeof NormalisedRectSchema>;

export const NormalisedPolygonSchema = z
  .array(NormalisedPointSchema)
  .min(3, 'Polygon needs at least 3 points');
export type NormalisedPolygon = z.infer<typeof NormalisedPolygonSchema>;

const LinkIdSchema = z.string().min(1).regex(/^[a-z0-9][a-z0-9_-]*$/i, {
  message: 'id must start with an alphanumeric character and use only letters, numbers, _ or -',
});

/** A point with optional Z elevation (0=foreground, 1=background). */
export const NormalisedPoint3DSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  z: z.number().min(0).max(1).optional(),
});
export type NormalisedPoint3D = z.infer<typeof NormalisedPoint3DSchema>;

export const NormalisedPolygon3DSchema = z
  .array(NormalisedPoint3DSchema)
  .min(3, 'Polygon needs at least 3 points');
export type NormalisedPolygon3D = z.infer<typeof NormalisedPolygon3DSchema>;

/** World-space pixel origin for edge-stitched rendering. */
export const WorldOriginSchema = z.object({
  x: z.number(),
  y: z.number(),
});
export type WorldOrigin = z.infer<typeof WorldOriginSchema>;

/** Hand-painted gradient overlay to forgive seam mismatches between edge-pan pairs. */
export const SeamStripSchema = z.object({
  texture: z.string().min(1),
  widthPx: z.number().int().positive(),
  anchor: z.enum(['left', 'right', 'top', 'bottom']),
});
export type SeamStrip = z.infer<typeof SeamStripSchema>;

/**
 * Edge zone — a region the walker can step into to trigger a scene transition.
 * Either `rect` or `polygon` is required (validated by the schema below).
 */
export const EdgeZoneSchema = z
  .object({
    /** Stable identifier, unique within the scene. */
    id: LinkIdSchema,
    /** Target scene key from `scenes[].key`. */
    targetScene: z.string().min(1),
    /** Which transition style to play when crossing. */
    transitionKind: TransitionKindSchema,
    /** Where the walker lands in the target scene (normalised). */
    spawn: NormalisedPointSchema,
    rect: NormalisedRectSchema.optional(),
    polygon: NormalisedPolygonSchema.optional(),
    /** Normalised rect for the door opening used by threshold-zoom transitions. */
    doorMask: NormalisedRectSchema.optional(),
    /** Optional millisecond override for the transition. */
    durationMs: z.number().int().positive().optional(),
    /** Seam strip overlay for edge-pan pairs. */
    seamStrip: SeamStripSchema.optional(),
  })
  .refine((z) => z.rect !== undefined || z.polygon !== undefined, {
    message: 'EdgeZone needs either rect or polygon',
    path: ['rect'],
  });
export type EdgeZone = z.infer<typeof EdgeZoneSchema>;

/**
 * Author-time hidden area. These are editor overlays and validation metadata,
 * not player-facing UI prompts or runtime reveal behavior.
 */
export const HiddenZoneSchema = z
  .object({
    id: LinkIdSchema,
    label: z.string().min(1).optional(),
    rect: NormalisedRectSchema.optional(),
    polygon: NormalisedPolygonSchema.optional(),
    initiallyVisibleInEditor: z.boolean().default(false),
    revealFlag: z.string().min(1).optional(),
  })
  .refine((z) => z.rect !== undefined || z.polygon !== undefined, {
    message: 'HiddenZone needs either rect or polygon',
    path: ['rect'],
  });
export type HiddenZone = z.infer<typeof HiddenZoneSchema>;

export const SpecialMechanicKindSchema = z.enum([
  'counterweight',
  'bell',
  'lantern-reveal',
]);
export type SpecialMechanicKind = z.infer<typeof SpecialMechanicKindSchema>;

export const SpecialMechanicLinkSchema = z.object({
  targetId: LinkIdSchema,
  role: z.enum(['activates', 'reveals', 'unlocks', 'balances', 'signals']).default('activates'),
});
export type SpecialMechanicLink = z.infer<typeof SpecialMechanicLinkSchema>;

const CounterweightParamsSchema = z.object({
  requiredWeight: z.number().positive().default(1),
  startsLowered: z.boolean().default(false),
});

const BellParamsSchema = z.object({
  tone: z.string().min(1).default('clear'),
  radius: z.number().min(0).max(1).default(0.18),
});

const LanternRevealParamsSchema = z.object({
  radius: z.number().min(0).max(1).default(0.2),
  revealHiddenZoneId: LinkIdSchema.optional(),
});

/**
 * Author-time special mechanic placement. Runtime behavior is intentionally
 * deferred; this records typed parameters and grouped links for designers.
 */
export const SpecialMechanicPlacementSchema = z.discriminatedUnion('kind', [
  z.object({
    id: LinkIdSchema,
    kind: z.literal('counterweight'),
    groupId: LinkIdSchema.optional(),
    anchor: NormalisedPointSchema,
    params: CounterweightParamsSchema.default({ requiredWeight: 1, startsLowered: false }),
    links: z.array(SpecialMechanicLinkSchema).default([]),
  }),
  z.object({
    id: LinkIdSchema,
    kind: z.literal('bell'),
    groupId: LinkIdSchema.optional(),
    anchor: NormalisedPointSchema,
    params: BellParamsSchema.default({ tone: 'clear', radius: 0.18 }),
    links: z.array(SpecialMechanicLinkSchema).default([]),
  }),
  z.object({
    id: LinkIdSchema,
    kind: z.literal('lantern-reveal'),
    groupId: LinkIdSchema.optional(),
    anchor: NormalisedPointSchema,
    params: LanternRevealParamsSchema.default({ radius: 0.2 }),
    links: z.array(SpecialMechanicLinkSchema).default([]),
  }),
]);
export type SpecialMechanicPlacement = z.infer<typeof SpecialMechanicPlacementSchema>;

/**
 * Interaction hotspot — a place the walker can stand to trigger something
 * (a door prompt, an inspectable object, a hearth that can be lit).
 *
 * The router persists `flag` once the interaction has been used so subsequent
 * visits can reflect the change (hearth stays lit, etc.).
 */
export const InteractionHotspotSchema = z
  .object({
    id: z.string().min(1),
    /** Player-facing prompt, e.g. "Press E to enter". */
    prompt: z.string().min(1),
    /** Persistence flag. Convention: `<sceneKey>.<id>.<verb>`. */
    flag: z
      .string()
      .min(1)
      .regex(/^[a-z0-9-]+\.[a-z0-9-]+\.[a-z0-9_-]+$/i, {
        message: 'flag must be `<sceneKey>.<id>.<verb>` lowercase-kebab',
      }),
    rect: NormalisedRectSchema.optional(),
    polygon: NormalisedPolygonSchema.optional(),
    /** Optional anchor for prompt UI, defaults to centre of rect/polygon. */
    anchor: NormalisedPointSchema.optional(),
    /** Whether the interaction can fire more than once. Default true. */
    repeatable: z.boolean().default(true),
  })
  .refine((z) => z.rect !== undefined || z.polygon !== undefined, {
    message: 'InteractionHotspot needs either rect or polygon',
    path: ['rect'],
  });
export type InteractionHotspot = z.infer<typeof InteractionHotspotSchema>;

/** Terrain type for material-aware footstep SFX. */
export const TerrainTypeSchema = z.enum(['grass', 'dirt', 'stone', 'leaves', 'water_edge']);
export type TerrainType = z.infer<typeof TerrainTypeSchema>;

/**
 * Material zone — a region with a specific terrain type for footstep SFX.
 * Uses rect or polygon, same as edge zones and interactions.
 */
export const MaterialZoneSchema = z
  .object({
    id: z.string().min(1),
    material: TerrainTypeSchema,
    rect: NormalisedRectSchema.optional(),
    polygon: NormalisedPolygonSchema.optional(),
  })
  .refine((z) => z.rect !== undefined || z.polygon !== undefined, {
    message: 'MaterialZone needs either rect or polygon',
    path: ['rect'],
  });
export type MaterialZone = z.infer<typeof MaterialZoneSchema>;

/** Entry-pan direction for epic camera scenes. */
export const EpicEntryPanSchema = z.object({
  direction: z.enum(['lateral', 'vertical', 'rise']),
  durationMs: z.number().int().positive().default(3000),
  /** Camera start offset from final position [dx, dy] in pixels. */
  offsetPx: z.tuple([z.number(), z.number()]),
  easing: z.enum(['easeOutCubic', 'easeInOutCubic']).default('easeInOutCubic'),
  firstVisitOnly: z.boolean().default(false),
});
export type EpicEntryPan = z.infer<typeof EpicEntryPanSchema>;

/** Epic camera overrides for scenes with grand scale. */
export const EpicCameraSchema = z.object({
  /** Default zoom level for this scene (e.g. 0.45–0.55 vs normal 1.0). */
  defaultZoom: z.number().min(0.2).max(1),
  /** Multiplier on perspective scale curve, shrinking the walker. */
  walkerScale: z.number().min(0.1).max(1).default(1),
  /** One-shot cinematic camera move on scene entry. */
  entryPan: EpicEntryPanSchema.optional(),
  /** Multiplier on parallax depth spread (e.g. 1.5–2.0). */
  parallaxAmplify: z.number().min(1).max(4).default(1),
});
export type EpicCamera = z.infer<typeof EpicCameraSchema>;

/** Day/night profile — per-phase ambient audio keys and optional layer overrides. */
export const DayNightProfileSchema = z.object({
  dawn:  z.object({ ambient: z.string().optional() }).optional(),
  day:   z.object({ ambient: z.string().optional() }).optional(),
  dusk:  z.object({ ambient: z.string().optional() }).optional(),
  night: z.object({ ambient: z.string().optional() }).optional(),
  /** Set false to disable the day/night cycle for this scene (e.g. interiors). */
  enabled: z.boolean().default(true),
});
export type DayNightProfile = z.infer<typeof DayNightProfileSchema>;

/**
 * Per-scene spec. The painted background art, walkable polygon, and walker
 * scale curve are referenced by `assetPath` — PaintedScene resolves them.
 */
export const PaintedSceneSpecSchema = z.object({
  /** Stable, kebab-case key. Used as Phaser scene-key suffix and persistence key. */
  key: z
    .string()
    .min(1)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'key must be lowercase-kebab'),
  /** Player-facing name, e.g. "The Path Home". */
  displayName: z.string().min(1),
  /** Mood line from the design doc — for journal / debug overlay. */
  mood: z.string().optional(),
  /** Music cue key (BootScene preloads, AmbientAudio plays on enter). */
  music: z.string().min(1),
  /** Optional ambient SFX bed key. */
  ambient: z.string().optional(),
  /** Path to per-scene asset bundle (background layers, polygon, scale curve). */
  assetPath: z.string().min(1),
  /** Default spawn point used when the scene is entered without a source zone. */
  defaultSpawn: NormalisedPointSchema,
  edgeZones: z.array(EdgeZoneSchema).default([]),
  interactions: z.array(InteractionHotspotSchema).default([]),
  /** Editor-only hidden authoring overlays. */
  hiddenZones: z.array(HiddenZoneSchema).default([]),
  /** Editor-only special mechanic placement metadata. */
  specialMechanics: z.array(SpecialMechanicPlacementSchema).default([]),
  /** Material zones for terrain-aware footstep SFX. First match wins; default is grass. */
  materialZones: z.array(MaterialZoneSchema).default([]),
  /** Pixel coords in a global coordinate space for edge-stitched rendering. */
  worldOrigin: WorldOriginSchema.optional(),
  /** Epic camera overrides for grand-scale scenes (AGE-1474). */
  epicCamera: EpicCameraSchema.optional(),
  /** Day/night cycle profile. Absent = use default cycle with base ambient. */
  dayNight: DayNightProfileSchema.optional(),
});
export type PaintedSceneSpec = z.infer<typeof PaintedSceneSpecSchema>;

/** Top-level manifest. */
export const JourneyManifestSchema = z
  .object({
    /** Manifest schema version. Bump on breaking shape changes. */
    version: z.literal(1),
    /** Which scene a fresh game starts in. Must be present in `scenes[]`. */
    initialScene: z.string().min(1),
    scenes: z.array(PaintedSceneSpecSchema).min(1),
  })
  .superRefine((manifest, ctx) => {
    const keys = new Set<string>();
    for (const scene of manifest.scenes) {
      if (keys.has(scene.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scenes'],
          message: `Duplicate scene key: ${scene.key}`,
        });
      }
      keys.add(scene.key);
    }
    if (!keys.has(manifest.initialScene)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['initialScene'],
        message: `initialScene "${manifest.initialScene}" not in scenes[]`,
      });
    }
    for (const scene of manifest.scenes) {
      const ids = new Set<string>();
      for (const zone of scene.edgeZones) {
        if (ids.has(zone.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scenes', scene.key, 'edgeZones'],
            message: `Duplicate zone id "${zone.id}" in scene ${scene.key}`,
          });
        }
        ids.add(zone.id);
        if (!keys.has(zone.targetScene)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scenes', scene.key, 'edgeZones', zone.id, 'targetScene'],
            message: `Edge zone "${zone.id}" targets unknown scene "${zone.targetScene}"`,
          });
        }
        if (zone.transitionKind === 'threshold-zoom' && !zone.doorMask) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scenes', scene.key, 'edgeZones', zone.id, 'doorMask'],
            message: `Threshold-zoom door "${zone.id}" is missing doorMask`,
          });
        }
      }
      const hiddenZoneIds = new Set<string>();
      for (const zone of scene.hiddenZones) {
        if (hiddenZoneIds.has(zone.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scenes', scene.key, 'hiddenZones'],
            message: `Duplicate hidden zone id "${zone.id}" in scene ${scene.key}`,
          });
        }
        hiddenZoneIds.add(zone.id);
      }
      const specialMechanicIds = new Set<string>();
      for (const mechanic of scene.specialMechanics) {
        if (specialMechanicIds.has(mechanic.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scenes', scene.key, 'specialMechanics'],
            message: `Duplicate special mechanic id "${mechanic.id}" in scene ${scene.key}`,
          });
        }
        specialMechanicIds.add(mechanic.id);
      }
      const linkableIds = new Set([
        ...ids,
        ...hiddenZoneIds,
        ...specialMechanicIds,
      ]);
      for (const mechanic of scene.specialMechanics) {
        for (const link of mechanic.links) {
          if (!linkableIds.has(link.targetId)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['scenes', scene.key, 'specialMechanics', mechanic.id, 'links'],
              message: `Special mechanic "${mechanic.id}" links to unknown placement "${link.targetId}"`,
            });
          }
        }
        if (
          mechanic.kind === 'lantern-reveal' &&
          mechanic.params.revealHiddenZoneId &&
          !hiddenZoneIds.has(mechanic.params.revealHiddenZoneId)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scenes', scene.key, 'specialMechanics', mechanic.id, 'params', 'revealHiddenZoneId'],
            message: `Lantern reveal "${mechanic.id}" references unknown hidden zone "${mechanic.params.revealHiddenZoneId}"`,
          });
        }
      }
      const interactionIds = new Set<string>();
      for (const interaction of scene.interactions) {
        if (interactionIds.has(interaction.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['scenes', scene.key, 'interactions'],
            message: `Duplicate interaction id "${interaction.id}" in scene ${scene.key}`,
          });
        }
        interactionIds.add(interaction.id);
      }
      const hasEdgePan = scene.edgeZones.some(
        (ez) => ez.transitionKind === 'edge-pan',
      );
      if (hasEdgePan && scene.worldOrigin === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scenes', scene.key, 'worldOrigin'],
          message: `Scene "${scene.key}" uses edge-pan transitions but is missing worldOrigin`,
        });
      }
    }
  });
export type JourneyManifest = z.infer<typeof JourneyManifestSchema>;

/** Validate a raw JSON object against the manifest schema; throws on mismatch. */
export function validateJourneyManifest(raw: unknown): JourneyManifest {
  return JourneyManifestSchema.parse(raw);
}

export interface JourneyManifestAuthoringIssue {
  severity: 'error' | 'warning';
  sceneKey?: string;
  path: string;
  message: string;
}

export interface JourneyManifestAuthoringValidationOptions {
  walkablePolygons?: Record<string, NormalisedPolygon>;
}

export function validateJourneyManifestForAuthoring(
  manifest: JourneyManifest,
  options: JourneyManifestAuthoringValidationOptions = {},
): JourneyManifestAuthoringIssue[] {
  const issues: JourneyManifestAuthoringIssue[] = [];
  const sceneKeys = new Set(manifest.scenes.map((scene) => scene.key));

  for (const scene of manifest.scenes) {
    const ids = new Set<string>();
    const edgeZones = scene.edgeZones ?? [];
    const hiddenZones = scene.hiddenZones ?? [];
    const specialMechanics = scene.specialMechanics ?? [];
    const addDuplicate = (id: string, path: string): void => {
      if (ids.has(id)) {
        issues.push({
          severity: 'error',
          sceneKey: scene.key,
          path,
          message: `Duplicate id "${id}" in scene ${scene.key}`,
        });
      }
      ids.add(id);
    };

    edgeZones.forEach((zone, index) => {
      addDuplicate(zone.id, `scenes.${scene.key}.edgeZones.${index}.id`);
      if (!sceneKeys.has(zone.targetScene)) {
        issues.push({
          severity: 'error',
          sceneKey: scene.key,
          path: `scenes.${scene.key}.edgeZones.${zone.id}.targetScene`,
          message: `Unresolved target scene "${zone.targetScene}"`,
        });
      }
      if (zone.transitionKind === 'threshold-zoom' && !zone.doorMask) {
        issues.push({
          severity: 'error',
          sceneKey: scene.key,
          path: `scenes.${scene.key}.edgeZones.${zone.id}.doorMask`,
          message: `Door "${zone.id}" is missing a doorMask`,
        });
      }
      const targetWalkable = options.walkablePolygons?.[zone.targetScene];
      if (targetWalkable && !pointInPolygon(zone.spawn, targetWalkable)) {
        issues.push({
          severity: 'error',
          sceneKey: scene.key,
          path: `scenes.${scene.key}.edgeZones.${zone.id}.spawn`,
          message: `Landing for "${zone.id}" is outside target scene walkable polygon`,
        });
      } else if (!targetWalkable) {
        issues.push({
          severity: 'warning',
          sceneKey: scene.key,
          path: `scenes.${scene.key}.edgeZones.${zone.id}.spawn`,
          message: `Landing walkability for "${zone.id}" was not checked; no walkable polygon supplied for ${zone.targetScene}`,
        });
      }
    });

    hiddenZones.forEach((zone, index) => {
      addDuplicate(zone.id, `scenes.${scene.key}.hiddenZones.${index}.id`);
    });

    specialMechanics.forEach((mechanic, index) => {
      addDuplicate(mechanic.id, `scenes.${scene.key}.specialMechanics.${index}.id`);
    });

    specialMechanics.forEach((mechanic) => {
      for (const link of mechanic.links) {
        if (!ids.has(link.targetId)) {
          issues.push({
            severity: 'error',
            sceneKey: scene.key,
            path: `scenes.${scene.key}.specialMechanics.${mechanic.id}.links`,
            message: `Orphaned special-mechanic link "${mechanic.id}" -> "${link.targetId}"`,
          });
        }
      }
      if (
        mechanic.kind === 'lantern-reveal' &&
        mechanic.params.revealHiddenZoneId &&
        !hiddenZones.some((zone) => zone.id === mechanic.params.revealHiddenZoneId)
      ) {
        issues.push({
          severity: 'error',
          sceneKey: scene.key,
          path: `scenes.${scene.key}.specialMechanics.${mechanic.id}.params.revealHiddenZoneId`,
          message: `Lantern reveal target "${mechanic.params.revealHiddenZoneId}" is not a hidden zone in ${scene.key}`,
        });
      }
    });
  }

  return issues;
}

function pointInPolygon(point: NormalisedPoint, polygon: NormalisedPolygon): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pi = polygon[i];
    const pj = polygon[j];
    const intersects = pi.y > point.y !== pj.y > point.y
      && point.x < ((pj.x - pi.x) * (point.y - pi.y)) / (pj.y - pi.y) + pi.x;
    if (intersects) inside = !inside;
  }
  return inside;
}
